import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { RouterModule, UrlTree } from '@angular/router';

export interface LinkItem {
  label: string;
  href: string | any[] | UrlTree | null | undefined;
  target?: string;
}
export interface InfoItem {
  label: string;
  value: string | LinkItem;
}

@Component({
  selector: 'app-info-card',
  standalone: true,
  imports: [CommonModule, SkeletonModule, RouterModule],
  template: `
    <div class="info-card" [class.single-column]="columns === 1" [class.three-columns]="columns === 3" [class.four-columns]="columns === 4" [class.last-span-2]="lastSpan2">
      <ng-container *ngIf="!loading; else loadingTpl">
        <div class="info-row" *ngFor="let row of infoItems">
          <div class="info-group" *ngFor="let item of row">
            <div class="label">{{item.label}}</div>
            @if(isLinkItem(item.value)){
              <div class="value">
                <a [routerLink]="item.value.href" [target]="item.value.target">{{item.value.label}}</a>
              </div>
            }
            @else {
              <div class="value">{{item.value || '-' }}</div>
            }
          </div>
        </div>
      </ng-container>

      <ng-template #loadingTpl>
        <div class="info-row" *ngFor="let i of [].constructor(loadingRows)">
          <div class="info-group" *ngFor="let j of columns === 1 ? [1] : columns === 3 ? [1,2,3]:columns === 4 ? [1,2,3,4] : [1,2]">
            <div class="label">
              <p-skeleton width="40%" height="16px"></p-skeleton>
            </div>
            <div class="value">
              <p-skeleton width="60%" height="20px"></p-skeleton>
            </div>
          </div>
        </div>
      </ng-template>
    </div>
  `,
  styles: [`
    .info-card {
      .info-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 2rem;
        margin-bottom: 1.25rem;

        &:last-child {
          margin-bottom: 0;
        }
      }

      &.single-column .info-row {
        grid-template-columns: 1fr;
      }

      &.three-columns .info-row {
        grid-template-columns: 1fr 1fr 1fr;
      }

      &.four-columns .info-row {
        grid-template-columns: 1fr 1fr 1fr 1fr;
      }

      &.last-span-2 .info-row:last-child {
        grid-template-columns: 1fr;
      }

      .info-group {
        .label {
          font-size: 14px;
          font-weight: 600;
          line-height: 21px;
          color: var(--p-primary-color);
          margin-bottom: 0.5rem;
        }

        .value {
          font-size: 14px;
          line-height: 21px;
          color: #334155;
          font-weight: 400;
        }
      }

      ::ng-deep {
        .p-skeleton {
          background-color: #E2E8F0;

          &::after {
            background: linear-gradient(90deg, #E2E8F0, #F1F5F9, #E2E8F0);
          }
        }
      }
    }
  `]
})
export class InfoCardComponent {
  @Input() columns: 1 | 2 | 3 | 4 = 2;
  @Input() lastSpan2?: boolean = false;
  @Input() loading?: boolean = false;
  @Input() loadingRows = 3;
  @Input() set items(value: InfoItem[]) {
    if (this.columns === 1) {
      this.infoItems = value.map(item => [item]);
    } else if (this.columns === 3) {
      this.infoItems = value.reduce((rows: InfoItem[][], item: InfoItem, index: number) => {
        if (index % 3 === 0) {
          rows.push([item]);
        } else {
          rows[rows.length - 1].push(item);
        }
        return rows;
      }, []);
    } else if (this.columns === 4) {
      this.infoItems = value.reduce((rows: InfoItem[][], item: InfoItem, index: number) => {
        if (index % 4 === 0) {
          rows.push([item]);
        } else {
          rows[rows.length - 1].push(item);
        }
        return rows;
      }, []);
    }else {
      this.infoItems = value.reduce((rows: InfoItem[][], item: InfoItem, index: number) => {
        if (index % 2 === 0) {
          rows.push([item]);
        } else {
          rows[rows.length - 1].push(item);
        }
        return rows;
      }, []);
    }
  }

  infoItems: InfoItem[][] = [];

  isLinkItem(value: any): value is LinkItem {
    return value && typeof value === 'object' && 'href' in value && 'label' in value;
  }
}
